/**
 * Dated delivery estimate.
 *
 * Turns a min and a max day count into a real arrival-date window and writes it into the block,
 * so the shopper reads "arrives Aug 8 to Aug 11" instead of a vague duration.
 *
 * The dates are computed here, in the browser, on every load. That is deliberate: Shopify caches
 * the product page HTML, so a date rendered in Liquid could be served for hours after it stopped
 * being true, and a stale date is worse than no date. Computing client-side keeps it fresh. If
 * this script never runs, the block keeps the plain-duration fallback that Liquid rendered, which
 * is always true and so is safe to leave standing.
 *
 * The date math below touches no DOM and is exported for tests. The wiring at the bottom runs only
 * in a browser. Nothing here makes a network request or stores anything.
 */

const WAREHOUSE_TZ_FALLBACK = 'America/Los_Angeles';

/**
 * Is this date a shipping day? The store ships Monday to Friday, so weekends do not count, either
 * as a ship day or as a day in transit.
 */
function isBusinessDay(date) {
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

function addDays(date, count) {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
}

/**
 * Advance `start` by `n` business days and return the landing date. The result is always a
 * business day: with n >= 1 the count only ticks on weekdays, and with n === 0 any weekend start
 * is rolled forward to the next Monday so a delivery is never promised on a day nothing moves.
 */
function addBusinessDays(start, n) {
  let date = new Date(start);
  let added = 0;
  while (added < n) {
    date = addDays(date, 1);
    if (isBusinessDay(date)) added += 1;
  }
  while (!isBusinessDay(date)) {
    date = addDays(date, 1);
  }
  return date;
}

/**
 * The arrival window. `today` is the store's current date; `fromToday` decides whether the count
 * starts the day the order is placed or the next day. min and max are day counts, swapped if they
 * arrive out of order so the window can never read backwards.
 */
function computeRange({ today, minDays, maxDays, fromToday = true }) {
  const lo = Math.min(minDays, maxDays);
  const hi = Math.max(minDays, maxDays);
  const base = fromToday ? new Date(today) : addDays(today, 1);
  return {
    min: addBusinessDays(base, lo),
    max: addBusinessDays(base, hi),
  };
}

/**
 * The store's own current date, not the shopper's. The cutoff and business-day logic belong to the
 * warehouse, so the calendar day is read in the warehouse time zone. Only the date matters, so the
 * value returned is a plain local-midnight Date: its weekday and its month/day are correct, and no
 * sub-day arithmetic is done that could trip over a daylight-saving boundary.
 */
function todayInTz(tz, now = new Date()) {
  const iso = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDate(date, { weekday = false } = {}) {
  const options = { month: 'short', day: 'numeric' };
  if (weekday) {
    options.weekday = 'short';
  }
  return new Intl.DateTimeFormat('en-US', options).format(date);
}

/**
 * Read one block's settings from its data attributes, compute the window, and write it in. A bad
 * or missing number leaves the Liquid fallback untouched rather than printing a wrong date.
 */
function render(el) {
  const minDays = Number.parseInt(el.dataset.minDays, 10);
  const maxDays = Number.parseInt(el.dataset.maxDays, 10);
  if (!Number.isFinite(minDays) || !Number.isFinite(maxDays)) {
    return;
  }

  const target = el.querySelector('.bh-de__range');
  if (!target) {
    return;
  }

  const tz = el.dataset.tz || WAREHOUSE_TZ_FALLBACK;
  const fromToday = el.dataset.fromToday !== 'false';
  const weekday = el.dataset.weekday === 'true';
  const separator = el.dataset.separator || 'to';

  const { min, max } = computeRange({
    today: todayInTz(tz),
    minDays,
    maxDays,
    fromToday,
  });

  const minStr = formatDate(min, { weekday });
  const maxStr = formatDate(max, { weekday });
  target.textContent =
    min.getTime() === max.getTime() ? minStr : `${minStr} ${separator} ${maxStr}`;
}

function init(root = document) {
  root.querySelectorAll('.bh-de').forEach(render);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  // A block added or restyled in the theme editor re-renders its section without reloading the
  // page, so recompute for whatever just came back.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
}

export { addBusinessDays, computeRange, formatDate, isBusinessDay, render, todayInTz };
